import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { EditorialTeam } from "@/components/editorial-team";
import { ReviewCasinoLogos } from "@/components/review-casino-logos";
import { EeatTrustBlocks } from "@/components/eeat-blocks";
import { CasinoList } from "@/components/casino-list";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/toto-uitbetaling";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/review/toto-uitbetaling";
const URL = `${SITE.url}${PATH}/`;
const META_TITLE = "TOTO Uitbetaling: Tijd, Limieten & Verificatie (2026)";

export const Route = createFileRoute("/review/toto-uitbetaling")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: META_TITLE },
        { name: "description", content: guide.description },
        { property: "og:title", content: META_TITLE },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("toto-uitbetaling"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            datePublished: "2026-10-19",
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "TOTO uitbetaling", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: TotoPage,
});

const TIMELINE = [
  { t: "Aanvraag", d: "Account > Saldo > Uitbetalen", v: "0 min" },
  { t: "Controle TOTO", d: "Rekening op eigen naam, limieten, eventueel documenten", v: "vóór goedkeuring" },
  { t: "Overboeking", d: "Instant betaling naar je bank", v: "meestal binnen 1 uur" },
  { t: "Bij je", d: "Bankrekening op jouw naam", v: "regulier binnen 24 uur" },
];

function PayoutTimelineInfographic() {
  return (
    <figure id="infographic-toto-uitbetaling-tijd" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">TOTO uitbetaling tijd: van aanvraag tot rekening</figcaption>
      <ol className="mt-5 grid gap-3 sm:grid-cols-4">
        {TIMELINE.map((s, i) => (
          <li key={s.t} className="rounded-lg border border-border bg-secondary/50 p-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
            <p className="mt-2 text-sm font-semibold text-foreground">{s.t}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
            <p className="mt-2 text-xs font-semibold text-success">{s.v}</p>
          </li>
        ))}
      </ol>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">Bron: klantenservice TOTO (toto.nl), gecontroleerd 19 oktober 2026. Geen eigen meting.</p>
    </figure>
  );
}

const FACTS = [
  ["Verwerkingstijd", "Meestal binnen 1 uur"],
  ["Reguliere termijn", "Binnen 24 uur"],
  ["Weekend / feestdag", "Ook mogelijk (instant betalen)"],
  ["Opwaarderen", "iDEAL (overgang naar Wero)"],
  ["Uitbetalen naar", "Eigen bankrekening, op jouw naam"],
  ["Kosten", "Geen uitbetaalkosten"],
  ["Minimum", "Verschilt per bron; zie je account"],
  ["Maximum", "Geen vast bedrag gepubliceerd"],
  ["Vergunning", "Kansspelautoriteit (Nederlandse Loterij)"],
  ["Eigen testdata", "Testronde gepland"],
];

function FactSheet() {
  return (
    <figure id="feiten-toto-uitbetaling" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">Feitenblad TOTO uitbetaling</figcaption>
      <dl className="mt-4 divide-y divide-border">
        {FACTS.map(([k, v]) => (
          <div key={k} className="flex flex-wrap justify-between gap-2 py-2.5 text-sm">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-medium text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">Bron: toto.nl/klantenservice (aanbiederinformatie). Eigen metingen volgen in de volgende testronde.</p>
    </figure>
  );
}

function TotoPage() {
  const budget = new LinkBudget(PATH);
  const casinos = getPublicCasinos();

  const takeaways = [
    "TOTO zegt zelf: een uitbetaling staat in de meeste gevallen binnen een uur op je rekening.",
    "Reguliere uitbetalingen worden binnen 24 uur verwerkt; controles kunnen dat verlengen.",
    "Uitbetalen gaat altijd naar je geverifieerde bankrekening op jouw naam.",
    "Opwaarderen kan alleen met iDEAL, dat stap voor stap overgaat in Wero.",
    "Een documentverzoek is de meest voorkomende reden voor vertraging.",
    "Het minimumbedrag verschilt per bron: controleer het in je account.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">TOTO uitbetaling</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        TOTO uitbetaling: hoe lang duurt het en wat kun je verwachten?
      </h1>

      <ReviewCasinoLogos casinos={casinos} contextLabel="De top 5 aanbieders uit onze casinolijst" />

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">Casino's met een snelle uitbetaling in onze tests</h2>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
          </section>

          <div className="mt-10">
            <KeyTakeaways items={takeaways} />
          </div>

          <div className="mt-10">
            <FactSheet />
          </div>

          <div className="mt-10">
            <PayoutTimelineInfographic />
          </div>

          <section className="mt-12" aria-label="Alles over de TOTO uitbetaling">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="TOTO uitbetaling"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over de TOTO uitbetaling
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
